import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getGroupById } from "../api/group";
import { getGameById } from "../api/game";
import { useTimer } from "../hooks/useTimer";
import { updateTime, setGroup, setGame } from "../redux/actions";
import { GAME_TIME_SECS, ROUTES } from "../utils/constants";
import { getPlayablePages } from "./EscapeRoomRenderer";
import {
	formatMinutes,
	formatSeconds,
	getTimer,
	updateStats,
	updateTimer,
	setLastTickNow,
	getLastTick,
	setLastTick 
} from "../utils/helpers";
import { resolveTopBarPrimary } from "../utils/gameColors";
import { postKpiEvent } from "../api/kpi";
import { groupLinkedGameId, resetPlaySessionToEnterCode } from "../utils/resetPlaySession";
import { resetSessionKpi, bumpSessionKpi, subscribeSessionKpi, getSessionKpiSnapshot } from "../utils/sessionKpiStore";
import Swal from "sweetalert2";

export default function TopBar() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const game = useSelector((state) => state.game);
	const group = useSelector((state) => state.group);
	const score = group?.score ?? 0;
	const playablePages = getPlayablePages(game?.escapeRoom?.pages || []);
	const currentPageIndex = Number.isFinite(group?.escapeRoomPageIndex) ? group.escapeRoomPageIndex : 0;
	const currentPage = playablePages[currentPageIndex] || playablePages[0];
	const timerRunsOnCurrentPage = currentPage?.timerRuns !== false;
	const headerPrimary = resolveTopBarPrimary(game, group);
	const showPlayerScore = game?.escapeRoom?.showPlayerScore !== false;
	const timeout = Number.isFinite(Number(game?.escapeRoom?.gameTimeSeconds))
		? Math.max(1, Math.round(Number(game.escapeRoom.gameTimeSeconds)))
		: GAME_TIME_SECS;

	const [time, setTime] = useState(() => {
		const saved = getTimer();
		const n = saved !== null ? Number(saved) : parseInt(group?.time || 0, 10);
		return Number.isFinite(n) ? n : 0;
	});

	const terminalNavDoneRef = useRef(false);
	useEffect(() => {
		terminalNavDoneRef.current = false;
	}, [group?._id]);

	const groupGameIdStr = useMemo(() => groupLinkedGameId(group), [group]);

	useEffect(() => {
		resetSessionKpi();
	}, [group?._id]);

	const sessionKpi = useSyncExternalStore(subscribeSessionKpi, getSessionKpiSnapshot, getSessionKpiSnapshot);

	/*
	useTimer(() => {
		if (!isACTIVE && getTimer()) isACTIVE = true;
		if (isACTIVE) handleTimer();
	}, 1000);
	*/
	useTimer(() => {
		handleTimer();
	}, 1000);

	/*
	const handleTimer = () => {
		if (!getTimer()) isACTIVE = false;
		else {
			setTime(time + 1);
			updateTimer(time);
		}
	};
	*/

	useEffect(() => {
	  if (getTimer() !== null && !getLastTick()) setLastTickNow();
	}, []);

	const handleTimer = () => {
		// Si no hay contador, no corremos
		if (getTimer() === null) return;
		if (!timerRunsOnCurrentPage) {
			setLastTickNow();
			return;
		}
		const now = Date.now();
		const prevTick = getLastTick() ?? now;
		const deltaMs  = now - prevTick;
		const deltaSec = Math.floor(deltaMs / 1000);
		if (deltaSec <= 0) return;
		setTime((prev) => {
			const next = prev + deltaSec;
			updateTimer(next);
			setLastTick(prevTick + deltaSec * 1000);
			return next;
		});
	};

	useEffect(() => {
		const onVis = () => {
			if (document.visibilityState === "visible") {
			handleTimer(); // actualiza de golpe lo pasado en 2º plano
			}
		};
		document.addEventListener("visibilitychange", onVis);
		return () => document.removeEventListener("visibilitychange", onVis);
	}, []);

	useEffect(() => {
		if (terminalNavDoneRef.current) return;
		if (
			time >= timeout &&
			group?._id &&
			group?.lastVisited !== ROUTES.GAMEOVER &&
			!game?.demo
		) {
			terminalNavDoneRef.current = true;
			dispatch(updateTime(time));
			updateStats({
				...group,
				time: time,
				lastVisited: ROUTES.GAMEOVER,
				finished: true,
			});
			// KPI: finish por timeout (best-effort)
			try {
				const gid = String(group?.game?._id || group?.game || "");
				const groupId = String(group?._id || "");
				if (gid && groupId) {
					bumpSessionKpi("finish");
					postKpiEvent({
						gameId: gid,
						groupId,
						eventType: "finish",
						reason: "timeout",
						totalScoreSnapshot: Number(group?.score ?? null),
						timestamp: new Date().toISOString(),
					}).catch(() => {});
				}
			} catch (_) {}
			navigate(ROUTES.GAMEOVER);
			return;
		}
		if (group?.finished) {
			terminalNavDoneRef.current = true;
			navigate(group?.lastVisited || ROUTES.RESULTS);
		}
	}, [time, group, game, navigate, dispatch, timeout]);

	useEffect(() => {
		if (!group?._id) return;
		const expelledDialog = async () => {
			try {
				await Swal.fire({
					icon: "warning",
					title: "Has sido expulsado",
					text: "Tu equipo ya no está en esta partida o se ha retirado el acceso.",
					confirmButtonText: "Entendido",
				});
			} catch (_) {
				// no-op
			}
			resetPlaySessionToEnterCode(dispatch, navigate);
		};
		getGroupById(group._id)
			.then((res) => {
				if (res.status === 200 && res.data) {
					dispatch(setGroup(res.data));
					return;
				}
				if (res.status === 404) {
					expelledDialog();
					return;
				}
				resetPlaySessionToEnterCode(dispatch, navigate);
			})
			.catch((err) => {
				if (err?.response?.status === 404) {
					expelledDialog();
					return;
				}
				resetPlaySessionToEnterCode(dispatch, navigate);
			});
	}, [location.pathname, location.search, group?._id, dispatch, navigate]);

	// Register pone game=null; al volver al juego Redux puede quedar sin juego y el header cae al rojo por defecto.
	useEffect(() => {
		if (!groupGameIdStr || groupGameIdStr === "undefined") return;
		if (game && String(game._id) === groupGameIdStr) return;
		getGameById(groupGameIdStr)
			.then((res) => {
				if (res.status !== 200 || !res.data) {
					resetPlaySessionToEnterCode(dispatch, navigate);
					return;
				}
				const g = res.data;
				if (!g.active) {
					resetPlaySessionToEnterCode(dispatch, navigate);
					return;
				}
				dispatch(setGame(g));
			})
			.catch(() => {
				resetPlaySessionToEnterCode(dispatch, navigate);
			});
	}, [groupGameIdStr, game?._id, dispatch, navigate]);

	return (
		<div
			className="py-2 er-topbar"
			style={{
				"--er-header-primary": headerPrimary,
				backgroundColor: headerPrimary,
			}}
		>
		<div className="row d-flex justify-content-between align-items-center legalFooter px-4">
			{/* Logo */}
			<div className="col-4 d-flex justify-content-start txtTexto2 txtRed">
				<span>{group?.fullName || "NOMBRE DEL EQUIPO"}</span>
				<span
					className="px-2 ms-3 rounded"
					style={{
						border: "1px var(--er-header-primary) dashed",
						borderRadius: "12px",
						minWidth: "60px",
						fontSize: "16px",
						fontWeight: "bold",
						color: "var(--er-header-primary)",
						backgroundColor: "rgba(255,255,255,0.9)"
					}}
				>equipo nº{group?.tableNum || 0}
				</span>
			</div>

			{/* Marcador de puntos */}
			<div className="col-4 d-flex align-items-center justify-content-center">
				{showPlayerScore ? (
				<div
					className="d-flex flex-column align-items-center justify-content-center px-4 py-2 rounded shadow-sm er-topbar-score-box"
					style={{
					borderRadius: "12px",
					minWidth: "140px",
					}}
				>
					<div className="txt42 bold" style={{ color: "white" }}>
					{score}
					</div>
			</div>
				) : null}
			</div>

			{/* Timer */}
			<div className="col-4 d-flex align-items-center justify-content-end er-topbar-actions">
				<div className="timer title1">
				{formatMinutes(time, timeout, group?.finished)}
				</div>
				<div className="title1 px-1 pt-1 blink bold">:</div>
				<div className="timer title1">
				{formatSeconds(time, timeout, group?.finished)}
				</div>
			</div>
			</div>

			{location.pathname === ROUTES.ESCAPE_ROOM ? (
				<div
					className="legalFooter px-4 py-2"
					style={{
						borderTop: "1px solid rgba(255,255,255,0.22)",
						background: "rgba(0,0,0,0.08)",
					}}
				>
					<div
						className="d-flex flex-wrap justify-content-center align-items-center gap-2 gap-md-4"
						style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.95)", letterSpacing: "0.02em" }}
					>
						<span>Páginas vistas: {sessionKpi.pageViews}</span>
						<span style={{ opacity: 0.5 }}>|</span>
						<span>Clics: {sessionKpi.buttonClicks}</span>
						<span style={{ opacity: 0.5 }}>|</span>
						<span>Respuestas: {sessionKpi.quizAnswers}</span>
						<span style={{ opacity: 0.5 }}>|</span>
						<span>Finalizaciones: {sessionKpi.finishes}</span>
					</div>
				</div>
			) : null}
		</div>
		);

}
